import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

/* Custom Code */
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import ManagedAccordionSection from "@insite/mobius/AccordionSection/ManagedAccordionSection";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { StyledClickable } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@store/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@store/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@store/Themes/common/mixins/common";
import { gridGapStyles } from "@store/Themes/common/mixins/grid";
import { linkAltStyles, linkStyles } from "@store/Themes/common/mixins/link";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import { accordionFooterSectionProps } from "@store/Themes/common/props/accordionFooter";

const enum fields {
    contentTitle = "contentTitle",
    firstColumnTitle = "firstColumnTitle",
    secondColumnTitle = "secondColumnTitle",
    thirdColumnTitle = "thirdColumnTitle",
    // Store Custom Code
    fourthColumnTitle = "fourthColumnTitle",
}

type Alignement = "left" | "center" | "right";

const baseLegalAlignStyles = (alignment: Alignement) => css`
    text-align: ${alignment};

    ul {
        text-align: ${alignment};
    }
`;

const legalAlignStyles = (alignment: Alignement) => css`
    ${baseLegalAlignStyles("center")}

    ${breakpoint("md")} {
        ${baseLegalAlignStyles(alignment)}
    }
`;

const footerLinkStyles = () => linkAltStyles();
/* Custom Code */

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // Custom Code
    widgetWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    columnsGridItem?: GridItemProps;
    columnsGridContainer?: GridContainerProps;
    columnGridItem?: GridItemProps;
    columnTitle?: TypographyPresentationProps;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    contentGridItem?: GridItemProps;
    contentTitle?: TypographyPresentationProps;
    legalWrapper?: InjectableCss;
    hidden?: HiddenProps;
    separatorGridItem?: GridItemProps;
    legalGridContainer?: GridContainerProps;
    legalLeftGridItem?: GridItemProps;
    legalRightGridItem?: GridItemProps;
    // Store Custom Links
    sublinksGridContainer?: GridContainerProps;
    sublinksGridItem?: GridItemProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetWrapper: {
        css: css`
            [href^="mailto:"] {
                word-break: break-word;
            }

            /* Fix CMS visual issues */
            [class^="ZoneStyle-"] {
                height: auto;
            }
        `,
    },
    innerWrapper: {
        css: siteWidthStyles(),
    },
    wrapper: {
        css: css`
            background-color: ${getColor("footer")};
            color: ${getColor("footer.contrast")};

            ${({ theme }) => css`
                ${breakpointValues(theme.design.layout.spacing.content, ["padding-top", "padding-bottom"])}
                font-size: ${theme.design.typography.size.smaller};
            `}
        `,
    },
    /* Store Custom Code - Start */
    contentGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-direction: column;
            position: relative;
            padding-top: 3rem;

            ${breakpoint("lg")} {
                padding-top: 0;
            }

            &::before {
                content: "";
                position: absolute;
                left: 0;
                right: 0;
                top: 1rem;
                height: ${pxToRem(1)};
                background-color: ${getColor("common.border")};

                ${breakpoint("lg")} {
                    top: 0;
                    left: -3rem;
                    width: ${pxToRem(1)};
                    height: 100%;
                    margin-left: -${pxToRem(1)};
                }
            }
        `,
    },
    /* Store Custom Code - End */
    contentTitle: {
        forwardAs: "p",
        css: css`
            ${spacingStyles(null, pxToRem(15))}
            ${({ theme }) => css`
                font-size: ${theme.design.typography.size.larger};
                line-height: ${theme.design.typography.lineHeight.semi};
            `}
        `,
    },
    separatorGridItem: {
        width: [0, 12, 12, 1, 1],
        css: css`
            &::before {
                content: "";
                position: relative;
                width: 100%;
                height: ${pxToRem(1)};
                background-color: ${getColor("common.border")};

                ${breakpoint("lg")} {
                    left: 50%;
                    width: ${pxToRem(1)};
                    height: 100%;
                    margin-left: -${pxToRem(1)};
                }
            }
        `,
    },
    columnsGridItem: {
        width: [12, 12, 12, 9, 9], // Store Custom Code
        css: css`
            li {
                margin-bottom: 1em;

                ${StyledClickable} {
                    ${footerLinkStyles()}
                }
            }
        `,
    },
    columnGridItem: {
        width: 3, // Store Custom Code
        css: css`
            flex-direction: column;
        `,
    },
    columnTitle: {
        forwardAs: "p",
        css: css`
            ${({ theme }) => css`
                ${spacingStyles(null, pxToRem(19))}
                font-size: ${theme.design.typography.size.base};
                font-weight: ${theme.design.typography.weight.bold};
            `}
        `,
    },
    hidden: {
        css: css`
            width: 100%;
        `,
    },
    legalWrapper: {
        css: css`
            padding: ${pxToRem(15, 0)};
            background-color: ${getColor("footer.alt")};
            color: ${getColor("footer.altContrast")};
            font-size: ${x => x.theme.design.typography.size.smallest};
        `,
    },
    legalGridContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(pxToRemArray([30]), pxToRemArray([6]))}

            li ${StyledClickable} {
                ${linkStyles("inherit", null, false, true)}
            }
        `,
    },
    /* Store Custom Code - Start */
    sublinksGridContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(pxToRemArray([30]), pxToRemArray([6]))}

            li ${StyledClickable} {
                ${linkStyles("inherit", null, false, true)}
                padding:${pxToRem(8, 15, 0, 15)};
            }
        `,
    },
    sublinksGridItem: {
        width: 12,
        css: legalAlignStyles("center"),
    },
    /* Store Custom Code - End */
    legalLeftGridItem: {
        width: [12, 12, 6, 6, 6],
        css: legalAlignStyles("left"),
    },
    legalRightGridItem: {
        width: [12, 12, 6, 6, 6],
        css: legalAlignStyles("right"),
    },
    accordionSection: accordionFooterSectionProps,
};

const FooterContainer: FC<OwnProps> = ({
    id,
    extendedStyles,
    // Custom Code
    fields: {
        contentTitle,
        firstColumnTitle,
        secondColumnTitle,
        thirdColumnTitle,
        fourthColumnTitle /* Store Custom Code */,
    },
}) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    /* Custom Code */
    return (
        <StyledWrapper {...styles.widgetWrapper}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.innerWrapper}>
                    <GridContainer {...styles.gridContainer}>
                        {/* Store Removed contentTitle & Seperator GridItems */}
                        <GridItem {...styles.columnsGridItem}>
                            <Hidden above="xs" {...styles.hidden}>
                                <Accordion headingLevel={6} {...styles.accordion}>
                                    <ManagedAccordionSection
                                        title={firstColumnTitle}
                                        uid="footer-accordion-1"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="FirstColumn" contentId={id} />
                                    </ManagedAccordionSection>
                                    <ManagedAccordionSection
                                        title={secondColumnTitle}
                                        uid="footer-accordion-2"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="SecondColumn" contentId={id} />
                                    </ManagedAccordionSection>
                                    <ManagedAccordionSection
                                        title={thirdColumnTitle}
                                        uid="footer-accordion-3"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="ThirdColumn" contentId={id} />
                                    </ManagedAccordionSection>
                                    {/* Store Custom Code - Start */}
                                    <ManagedAccordionSection
                                        title={fourthColumnTitle}
                                        uid="footer-accordion-4"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="FourthColumn" contentId={id} />
                                    </ManagedAccordionSection>
                                    {/* Store Custom Code - End */}
                                </Accordion>
                            </Hidden>
                            <Hidden below="sm" {...styles.hidden}>
                                <GridContainer {...styles.columnsGridContainer}>
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{firstColumnTitle}</Typography>
                                        <Zone zoneName="FirstColumn" contentId={id} />
                                    </GridItem>
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{secondColumnTitle}</Typography>
                                        <Zone zoneName="SecondColumn" contentId={id} />
                                    </GridItem>
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{thirdColumnTitle}</Typography>
                                        <Zone zoneName="ThirdColumn" contentId={id} />
                                    </GridItem>
                                    {/* Store Custom Code - Start */}
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{fourthColumnTitle}</Typography>
                                        <Zone zoneName="FourthColumn" contentId={id} />
                                    </GridItem>
                                    {/* Store Custom Code - End */}
                                </GridContainer>
                            </Hidden>
                        </GridItem>
                        {/* Store Custom Code - Start */}
                        <GridItem {...styles.contentGridItem}>
                            <Zone zoneName="FirstContent" contentId={id} />
                            <br />
                            {contentTitle ? <Typography {...styles.contentTitle}>{contentTitle}</Typography> : null}
                            <Zone zoneName="Content" contentId={id} />
                        </GridItem>
                        {/* Store Custom Code - End */}
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.legalWrapper}>
                <StyledWrapper {...styles.innerWrapper}>
                    <GridContainer {...styles.legalGridContainer}>
                        <GridItem {...styles.legalLeftGridItem}>
                            <Zone zoneName="LegalLeft" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.legalRightGridItem}>
                            <Zone zoneName="LegalRight" contentId={id} />
                            {/* Store Custom Code - Start */}
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.sublinksGridContainer}>
                        <GridItem {...styles.sublinksGridItem}>
                            <Zone zoneName="sublinks" contentId={id} />
                            {/* Store Custom Code - End */}
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
    /* Custom Code */
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        /* Custom Code */
        fieldDefinitions: [
            {
                name: fields.contentTitle,
                displayName: "Content title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.firstColumnTitle,
                displayName: "First Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            {
                name: fields.secondColumnTitle,
                displayName: "Second Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            {
                name: fields.thirdColumnTitle,
                displayName: "Third Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            /* Store Custom Code - Start */
            {
                name: fields.fourthColumnTitle,
                displayName: "Fourth Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            /* Store Custom Code - End */
        ],
        /* Custom Code */
    },
};

export default footerContainer;
